import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trophy, Clock, CheckCircle, Home, ArrowRight, TrendingUp, Award, Share2, Link2, Check, Trash2, X as XIcon, ImageDown, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GoProBanner } from '@/components/GoProBanner';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { RecentlyDeletedDialog } from '@/components/RecentlyDeletedDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

export default function StatsHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isPro, isAuthenticated, isLoading } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSeasonTarget, setDeleteSeasonTarget] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [brandTitle, setBrandTitle] = useState('Season Leaderboard');
  const [brandSubtitle, setBrandSubtitle] = useState('');
  const [brandColor, setBrandColor] = useState('#F59E0B');
  const [brandLogo, setBrandLogo] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [newSeasonName, setNewSeasonName] = useState('');
  const [showSeasonForm, setShowSeasonForm] = useState(false);

  // Read event_id from URL query params (shortcut from ScheduleView)
  const searchParams = new URLSearchParams(location.search);
  const eventIdFilter = searchParams.get('event_id') || '';
  const [eventFilterName, setEventFilterName] = useState('');

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!isPro) return;
    fetchGames();
    fetchShareToken();
    fetchSeasons();
    if (eventIdFilter) fetchEventName(eventIdFilter);
  }, [isAuthenticated, isPro, isLoading, eventIdFilter]);

  const fetchEventName = async (eventId) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/${eventId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setEventFilterName(data.name || '');
      }
    } catch {}
  };

  const clearEventFilter = () => {
    navigate('/stats', { replace: true });
    setEventFilterName('');
  };

  // Share Player Card — picker state. When a game row's Share button is clicked,
  // we open a dialog listing both teams' players. Tapping a player generates the
  // 1080x1080 PNG via the public career-card endpoint (using the user's existing
  // leaderboard share_token, auto-vivified by GET /leaderboard/share).
  const [shareCardGame, setShareCardGame] = useState(null);
  const [shareCardWorking, setShareCardWorking] = useState('');

  const handleSharePlayerCard = async (game, team, player) => {
    const key = `${team}:${player.name}`;
    setShareCardWorking(key);
    try {
      // Ensure we have a share token (idempotent)
      const token = localStorage.getItem('session_token');
      const shareRes = await fetch(`${API_URL}/api/stats/leaderboard/share`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const shareData = await shareRes.json();
      const shareToken = shareData.share_token;
      if (!shareToken) throw new Error('Could not provision share link');

      const teamName = team === 'a' ? game.team_a.name : game.team_b.name;
      const params = new URLSearchParams({ team: teamName, name: player.name });
      // Pass event_id so the server can pick the right sport-aware stats
      // (Goals/Assists for soccer, Kills/Aces for volleyball, PPG/RPG for
      // basketball). Without this the card falls back to basketball defaults.
      if (game.event_id) params.append('event_id', game.event_id);
      const cardUrl = `${API_URL}/api/stats/leaderboard/${shareToken}/career/card.png?${params.toString()}`;
      const careerLink = `${window.location.origin}/stats/leaderboard/${shareToken}/career?${params.toString()}`;

      // Fetch the PNG so Web Share API can use it as a file (mobile-friendly)
      // and so we can offer a clean download fallback.
      const imgRes = await fetch(cardUrl);
      if (!imgRes.ok) throw new Error('Card render failed');
      const blob = await imgRes.blob();

      const file = new File([blob], `${player.name.replace(/\s+/g, '-')}-card.png`, { type: 'image/png' });
      // 1) Web Share with files (iOS/Android)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `${player.name} — Career Card`, text: careerLink });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      // 2) Open in a new tab so the user can long-press / right-click to save
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${player.name.replace(/\s+/g, '-')}-career-card.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      // Also copy the public career link
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(careerLink);
          toast.success('Card downloaded — career link copied to clipboard');
        } else {
          toast.success('Card downloaded');
        }
      } catch {
        toast.success('Card downloaded');
      }
    } catch (e) {
      toast.error(e.message || 'Could not generate card');
    } finally {
      setShareCardWorking('');
    }
  };

  const fetchSeasons = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/seasons`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setSeasons(await res.json());
    } catch {}
  };

  const createSeason = async () => {
    if (!newSeasonName.trim()) return;
    const token = localStorage.getItem('session_token');
    const res = await fetch(`${API_URL}/api/stats/seasons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name: newSeasonName })
    });
    if (res.ok) {
      toast.success(`Season "${newSeasonName}" created`);
      setNewSeasonName('');
      setShowSeasonForm(false);
      fetchSeasons();
    }
  };

  const fetchShareToken = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/leaderboard/share`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.share_token) {
          setShareToken(data.share_token);
          setBrandTitle(data.title || 'Season Leaderboard');
          setBrandSubtitle(data.subtitle || '');
          setBrandColor(data.accent_color || '#F59E0B');
          setBrandLogo(data.logo_url || '');
        }
      }
    } catch {}
  };

  const fetchGames = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/games`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setGames(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/games/join/${joinCode.trim()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        navigate(`/stats/game/${data.game.id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Game not found');
      }
    } catch (e) { toast.error('Failed to join'); }
    finally { setJoining(false); }
  };

  const handleDeleteGame = (game, e) => {
    e.stopPropagation();
    setDeleteTarget(game);
  };

  const confirmDeleteGame = async (game) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/games/${game.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`"${game.name}" moved to Recently deleted`);
        setGames(prev => prev.filter(g => g.id !== game.id));
        setDeleteTarget(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete game');
      }
    } catch { toast.error('Network error'); }
  };

  const confirmDeleteSeason = async () => {
    const season = deleteSeasonTarget;
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/seasons/${season.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`Season "${season.name}" moved to Recently deleted`);
        setGames(prev => prev.filter(g => g.season_id !== season.id));
        setSeasons(prev => prev.filter(s => s.id !== season.id));
        setSelectedSeason('');
        setDeleteSeasonTarget(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete season');
      }
    } catch { toast.error('Network error'); }
  };

  const handleTrashRestored = () => {
    fetchGames();
    fetchSeasons();
  };

  if (!isPro) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <GoProBanner />
        <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 text-[#00247D] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#00247D] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>BASKETBALL STATS</h2>
            <p className="text-slate-500 mb-6">Track full game statistics with our Easy Stats-style interface. Available for Pro subscribers.</p>
            <Button onClick={() => navigate('/pricing')} className="bg-[#00247D] hover:bg-[#001A5C] text-white">
              Upgrade to Pro
            </Button>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-2 sm:py-0 sm:h-16 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-slate-600 px-2 sm:px-3 shrink-0">
              <Home className="w-5 h-5" />
            </Button>
            <h1 className="text-base sm:text-xl font-bold text-[#00247D] truncate" style={{ fontFamily: 'Barlow Condensed' }}>
              BASKETBALL STATS
            </h1>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 order-3 sm:order-none w-full sm:w-auto overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
            <Button variant="outline" size="sm" onClick={() => navigate('/stats/season')} data-testid="season-stats-btn"
              className="shrink-0">
              <TrendingUp className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Season</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/stats/rankings')} data-testid="rankings-btn"
              className="shrink-0">
              <Trophy className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Rankings</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/stats/awards${eventIdFilter ? `?event_id=${encodeURIComponent(eventIdFilter)}` : ''}`)} data-testid="awards-btn"
              className="shrink-0">
              <Award className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Awards</span>
            </Button>
            <Button size="sm" onClick={() => navigate('/stats/setup')}
              className="bg-[#00247D] hover:bg-[#001A5C] text-white shrink-0"
              data-testid="new-game-btn">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Game</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {/* Event Filter Banner (from ScheduleView shortcut) */}
        {eventIdFilter && (
          <Card className="mb-4 border-2 border-[#00247D] bg-blue-50/50" data-testid="event-filter-banner">
            <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
              <div className="flex items-start sm:items-center gap-2 text-sm min-w-0">
                <Trophy className="w-4 h-4 text-[#00247D] shrink-0 mt-0.5 sm:mt-0" />
                <span className="text-slate-600 shrink-0">Showing stats for event:</span>
                <span className="font-semibold text-[#00247D] break-words" data-testid="filtered-event-name">
                  {eventFilterName || 'Loading…'}
                </span>
              </div>
              <Button size="sm" variant="ghost" onClick={clearEventFilter} data-testid="clear-event-filter"
                className="text-slate-500 hover:text-slate-800 shrink-0 self-end sm:self-auto">
                <XIcon className="w-3.5 h-3.5 mr-1" /> Show all stats
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Join Game */}
        <Card className="mb-4 border-dashed border-2 border-slate-300">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
              <Input
                placeholder="Enter game code..."
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="w-full sm:max-w-[200px] uppercase tracking-widest font-mono"
                data-testid="join-code-input"
              />
              <Button variant="outline" onClick={handleJoin} disabled={joining} data-testid="join-game-btn"
                className="shrink-0">
                <ArrowRight className="w-4 h-4 mr-1" /> Join Game
              </Button>
            </div>
            <span className="text-xs text-slate-500 sm:ml-2">
              Join another device's game to stat a different team
            </span>
          </CardContent>
        </Card>

        {/* Season Selector */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
          <Select value={selectedSeason || 'all'} onValueChange={v => setSelectedSeason(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-[200px] max-w-[220px]" data-testid="season-select">
              <SelectValue placeholder="All Seasons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Seasons</SelectItem>
              {seasons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {!showSeasonForm ? (
            <Button variant="outline" size="sm" onClick={() => setShowSeasonForm(true)} data-testid="new-season-btn"
              className="shrink-0">
              <Plus className="w-3 h-3 mr-1" /> New Season
            </Button>
          ) : (
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Input value={newSeasonName} onChange={e => setNewSeasonName(e.target.value)} placeholder="Season name..." className="flex-1 sm:w-40 h-9 min-w-[140px]" data-testid="season-name-input" />
              <Button size="sm" className="bg-[#00247D] hover:bg-[#001A5C] text-white h-9" onClick={createSeason}>Create</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowSeasonForm(false)}>Cancel</Button>
            </div>
          )}
          {selectedSeason && seasons.some(s => s.id === selectedSeason) && (
            <Button variant="outline" size="sm" className="shrink-0 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={() => setDeleteSeasonTarget(seasons.find(s => s.id === selectedSeason))}
              data-testid="delete-season-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Delete Season
            </Button>
          )}
          <Button variant="ghost" size="sm" className="shrink-0 sm:ml-auto text-slate-500 hover:text-[#00247D]"
            onClick={() => setShowTrash(true)} data-testid="recently-deleted-btn">
            <RotateCcw className="w-3 h-3 mr-1" /> Recently deleted
          </Button>
        </div>

        {/* Games List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-700 mb-2">No games yet</h3>
            <p className="text-slate-500 mb-6">Start tracking basketball stats for your games</p>
            <Button onClick={() => navigate('/stats/setup')} className="bg-[#00247D] hover:bg-[#001A5C] text-white">
              <Plus className="w-4 h-4 mr-2" /> Create First Game
            </Button>
          </div>
        ) : (
          (() => {
            const filteredGames = games.filter(g => (!selectedSeason || g.season_id === selectedSeason) && (!eventIdFilter || g.event_id === eventIdFilter));
            if (filteredGames.length === 0) {
              return (
                <div className="text-center py-16" data-testid="no-filtered-games">
                  <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-700 mb-2">No games match the current filter</h3>
                  <p className="text-slate-500 mb-6">
                    {eventIdFilter
                      ? `No stats games have been created for ${eventFilterName || 'this event'} yet.`
                      : 'Try switching seasons or creating a new game.'}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <Button onClick={() => navigate('/stats/setup' + (eventIdFilter ? `?event_id=${eventIdFilter}` : ''))}
                      className="bg-[#00247D] hover:bg-[#001A5C] text-white">
                      <Plus className="w-4 h-4 mr-2" /> Create Game
                    </Button>
                    {eventIdFilter && (
                      <Button variant="outline" onClick={clearEventFilter}>Show All Stats</Button>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div className="space-y-3">
                {filteredGames.map(game => (
              <Card key={game.id} className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(game.status === 'active' ? `/stats/game/${game.id}` : `/stats/summary/${game.id}`)}
                data-testid={`game-card-${game.id}`}>
                <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-slate-800 truncate">{game.name}</h3>
                    <p className="text-sm text-slate-500 break-words">
                      {game.team_a.name} vs {game.team_b.name} &middot; {game.date}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0">
                    {game.status === 'active' ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                        <Clock className="w-3 h-3" /> Live
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                        <CheckCircle className="w-3 h-3" /> {game.entry_mode === 'post_game' ? 'Imported' : 'Completed'}
                      </span>
                    )}
                    {game.join_code && game.status === 'active' && (
                      <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">
                        {game.join_code}
                      </span>
                    )}
                    {game.status === 'completed' && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setShareCardGame(game); }}
                        className="p-1.5 rounded-md text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                        title="Share player card"
                        data-testid={`share-card-game-${game.id}`}>
                        <ImageDown className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => handleDeleteGame(game, e)}
                      className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Delete game"
                      data-testid={`delete-game-${game.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
              </div>
            );
          })()
        )}
      </div>

      {/* Share Player Card picker dialog */}
      <Dialog open={!!shareCardGame} onOpenChange={(open) => { if (!open) setShareCardGame(null); }}>
        <DialogContent className="max-w-md" data-testid="share-card-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageDown className="w-5 h-5 text-amber-500" />
              Share Player Card
            </DialogTitle>
          </DialogHeader>
          {shareCardGame && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Pick a player to generate their 1080×1080 career card. The PNG works on Instagram, Twitter/X, WhatsApp — and includes a "Hosted by …" footer linking back to your leaderboard.
              </p>
              {[
                { team: 'a', name: shareCardGame.team_a.name, players: shareCardGame.team_a.players || [] },
                { team: 'b', name: shareCardGame.team_b.name, players: shareCardGame.team_b.players || [] },
              ].map(({ team, name, players }) => (
                <div key={team}>
                  <h4 className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">{name}</h4>
                  {players.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No players on this team.</p>
                  ) : (
                    <div className="grid grid-cols-1 gap-1.5">
                      {players.map(p => {
                        const key = `${team}:${p.name}`;
                        const isWorking = shareCardWorking === key;
                        return (
                          <button
                            key={p.id || key}
                            type="button"
                            disabled={isWorking || !!shareCardWorking}
                            onClick={() => handleSharePlayerCard(shareCardGame, team, p)}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/30 disabled:opacity-50 transition-colors text-left"
                            data-testid={`share-card-player-${team}-${p.id || p.name}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              {p.jersey_number && (
                                <span className="font-mono text-xs text-slate-500 w-7 text-center">#{p.jersey_number}</span>
                              )}
                              <span className="text-sm font-medium text-slate-700 truncate">{p.name}</span>
                            </div>
                            {isWorking ? (
                              <span className="text-xs text-amber-600 font-semibold">Generating…</span>
                            ) : (
                              <ImageDown className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title="Delete game?"
        itemName={deleteTarget?.name}
        description="and its play-by-play stats and AI recap will move to Recently deleted. You can restore it within 7 days."
        confirmLabel="Delete Game"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => confirmDeleteGame(deleteTarget)}
        testId="delete-game"
      />
      <ConfirmDeleteDialog
        open={!!deleteSeasonTarget}
        title="Delete season?"
        itemName={deleteSeasonTarget?.name}
        description={`and all ${games.filter(g => g.season_id === deleteSeasonTarget?.id).length} game(s) in it will move to Recently deleted. You can restore the whole season within 7 days.`}
        confirmLabel="Delete Season"
        onClose={() => setDeleteSeasonTarget(null)}
        onConfirm={confirmDeleteSeason}
        testId="delete-season"
      />
      <RecentlyDeletedDialog open={showTrash} onClose={() => setShowTrash(false)} group="stats" onRestored={handleTrashRestored} />
    </div>
  );
}
